import React, { useContext } from "react";
import { useParams, Link } from "react-router-dom";
import LoadingSpinner from "../common/LoadingSpinner.js";
import UserContext from "../context/UserContext.js";
import useFetchAvgRating from "../hooks/useFetchAvgRating.js";
import useFetchSpace from "../hooks/useFetchSpace.js";
import useLikeSpace from "../hooks/useLikeSpace.js";
import useMarkSpaceVisited from "../hooks/useMarkSpaceVisited.js";
import "./Spaces.css";

/** Space Detail page.
 *
 * Renders information about space.
 *
 * Routed at /spaces/:title
 *
 * Routes -> SpaceDetail
 */

const SpaceDetail = () => {
  const { currentUser } = useContext(UserContext);
  const { title } = useParams();
  console.debug("SpaceDetail", "title=", title);

  const space = useFetchSpace(title);
  const avgRating = useFetchAvgRating(title);
  const { isLiked, handleLikeClick } = useLikeSpace(currentUser, title);
  const { visited, visitDate, handleVisitClick } = useMarkSpaceVisited(currentUser, title);

  if (!space) return <LoadingSpinner />;

  return (
    <div className="Space-Detail-Container">
      <h1 className="Space-Title">{space.title}</h1>

      <div className="space-detail-actions">
        <button
          className={`space-detail-btn ${isLiked ? "space-detail-btn-liked" : ""}`}
          onClick={handleLikeClick}
        >
          {isLiked ? "♥ Liked" : "♡ Like"}
        </button>
        <button
          className="space-detail-btn"
          onClick={handleVisitClick}
          disabled={visited}
        >
          {visited ? `✓ Visited ${visitDate}` : "Mark as Visited"}
        </button>
        <Link to={`/ratings/${currentUser.username}/spaces/${space.title}/new`}>
          <button className="space-detail-btn">Rate this Space</button>
        </Link>
        {currentUser.isAdmin && (
          <Link to={`/spaces/${space.title}/edit`}>
            <button className="space-detail-btn">Edit</button>
          </Link>
        )}
      </div>

      {space.image_url && (
        <img className="Space-Image" src={space.image_url} alt={space.title} />
      )}

      <div className="space-detail-info">
        {space.description && (
          <p className="space-detail-description">{space.description}</p>
        )}

        <div className="space-detail-meta">
          {space.est_year && (
            <div className="space-detail-meta-item">
              <span className="space-detail-meta-label">Project Year</span>
              <span className="space-detail-meta-value">{space.est_year}</span>
            </div>
          )}
          {avgRating !== null && !isNaN(avgRating) && (
            <div className="space-detail-meta-item">
              <span className="space-detail-meta-label">Average Rating</span>
              <span className="space-detail-meta-value">★ {parseFloat(avgRating).toFixed(1)}</span>
            </div>
          )}
          {space.category && (
            <div className="space-detail-meta-item">
              <span className="space-detail-meta-label">Category</span>
              <Link className="space-detail-meta-value" to={`/categories/${space.category}`}>
                {space.category}
              </Link>
            </div>
          )}
          {space.address && (
            <div className="space-detail-meta-item">
              <span className="space-detail-meta-label">Address</span>
              <Link className="space-detail-meta-value" to={`/map/${title}`}>
                {space.address}
              </Link>
            </div>
          )}
        </div>

        <div className="space-detail-links">
          <Link className="btn-outline-custom" to={`/comments/spaces/${space.title}`}>
            View Comments
          </Link>
          <Link className="btn-outline-custom" to={`/map/${title}`}>
            View on Map
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SpaceDetail;
